import {
  buildDataset,
  buildFields,
  extractArray,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import {
  buildMetricFieldEntries,
  constrainMetricFieldsToFamilies,
  hasCoRealizedMetricFamilyPair,
  resolveMetricChartProfile,
  resolveRealizedDefaultMetricKey,
} from "@/lib/ai/visualization/data/adapters/metric-binding";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";
import {
  buildGroupedTimeseriesRows,
  resolveUaGroupBy,
  resolveUaGroupLabelDomain,
} from "./aggregated-grouping";
import { UA_BASE_METRICS } from "./aggregated-metrics";

const UA_GROUPS_TIMESERIES_ROWS_PROFILE: NonNullable<VizDataset["profile"]> = {
  datasetFamily: "ua.aggregated",
  variant: "groups",
  compareMode: "none",
  supportedViews: [
    "bar-ranking",
    "bar-comparison",
    "time-series-line",
    "time-series-grouped-line",
    "time-series-area",
    "time-series-composed",
    "scatter-plot",
    "table",
  ],
};

const UA_GROUPS_TIMESERIES_COMPARE_ROWS_PROFILE: NonNullable<
  VizDataset["profile"]
> = {
  datasetFamily: "ua.aggregated",
  variant: "groups",
  compareMode: "period",
  supportedViews: [
    "bar-ranking",
    "bar-comparison",
    "time-series-line",
    "time-series-area",
    "time-series-composed",
    "scatter-plot",
    "table",
  ],
};

const UA_GROUPED_DEFAULTS: NonNullable<VizDataset["defaults"]> = {
  x: "date",
  series: "group",
  category: "group",
  value: "sessionCount",
  y: ["sessionCount"],
};

function buildUaGroupedRowsDataset(args: {
  source: VizDataset["source"];
  rows: VizDataset["rows"];
  groupLabelDomain: ReturnType<typeof resolveUaGroupLabelDomain>;
}): VizDataset {
  return buildDataset({
    id: "ua-agg-groups-timeseries-rows",
    profile: UA_GROUPS_TIMESERIES_ROWS_PROFILE,
    fields: buildFields([
      { key: "date", role: "x", type: "date" },
      {
        key: "group",
        role: "series",
        type: "string",
        ...(args.groupLabelDomain
          ? { labelDomain: args.groupLabelDomain }
          : null),
      },
      ...buildMetricFieldEntries(UA_BASE_METRICS),
    ]),
    rows: args.rows,
    defaults: UA_GROUPED_DEFAULTS,
    source: args.source,
  });
}

function buildUaGroupedCompareRowsDataset(args: {
  source: VizDataset["source"];
  rows: VizDataset["rows"];
  groupLabelDomain: ReturnType<typeof resolveUaGroupLabelDomain>;
  requestedMetricFamilies: ReadonlySet<string> | null;
}): VizDataset {
  const fields = constrainMetricFieldsToFamilies({
    fields: buildFields([
      { key: "date", role: "x", type: "date" },
      {
        key: "group",
        role: "series",
        type: "string",
        ...(args.groupLabelDomain
          ? { labelDomain: args.groupLabelDomain }
          : null),
      },
      { key: "period", role: "dim", type: "string" },
      ...buildMetricFieldEntries(UA_BASE_METRICS),
    ]),
    metricFamilies: args.requestedMetricFamilies,
  });
  const defaultMetricKey =
    resolveRealizedDefaultMetricKey({
      fields,
      rows: args.rows,
      preferredMetricFamily: "sessionCount",
    }) ?? "sessionCount";
  const supportsScatterPlot = hasCoRealizedMetricFamilyPair({
    rows: args.rows,
    fields,
  });

  return buildDataset({
    id: "ua-agg-groups-timeseries-compare-rows",
    profile: resolveMetricChartProfile({
      profile: UA_GROUPS_TIMESERIES_COMPARE_ROWS_PROFILE,
      rows: args.rows,
      fields,
      supportsScatterPlot,
    }),
    fields,
    rows: args.rows,
    defaults: {
      ...UA_GROUPED_DEFAULTS,
      value: defaultMetricKey,
      y: [defaultMetricKey],
    },
    source: args.source,
  });
}

/**
 * UA aggregated groupedData/comparingGroupedData から grouped 系 dataset 群を構築する。
 *
 * @remarks
 * groupBy 解決・label domain 解決・period current/previous 付与をこの層へ集約し、
 * adapter 本体から grouped 固有の分岐を分離する。
 */
export function buildUaGroupedDatasets(args: {
  output: Record<string, unknown>;
  source: VizDataset["source"];
  requestedMetricFamilies: ReadonlySet<string> | null;
}): VizDataset[] {
  const grouped = extractArray(args.output, "groupedData");
  const comparingGrouped = extractArray(args.output, "comparingGroupedData");
  const resolvedGroupBy = resolveUaGroupBy({
    output: args.output,
    grouped,
    comparingGrouped,
  });
  const groupLabelDomain = resolveUaGroupLabelDomain(resolvedGroupBy);

  const datasets: VizDataset[] = [];
  const groupedTimeseriesRows = buildGroupedTimeseriesRows({
    items: grouped,
    groupBy: resolvedGroupBy,
    metrics: UA_BASE_METRICS,
  });
  if (groupedTimeseriesRows.length > 0) {
    datasets.push(
      buildUaGroupedRowsDataset({
        source: args.source,
        rows: groupedTimeseriesRows,
        groupLabelDomain,
      }),
    );
  }

  const groupedCurrentRows = buildGroupedTimeseriesRows({
    items: grouped,
    groupBy: resolvedGroupBy,
    metrics: UA_BASE_METRICS,
    period: "current",
  });
  const groupedPreviousRows = buildGroupedTimeseriesRows({
    items: comparingGrouped,
    groupBy: resolvedGroupBy,
    metrics: UA_BASE_METRICS,
    period: "previous",
  });
  if (groupedCurrentRows.length > 0 && groupedPreviousRows.length > 0) {
    datasets.push(
      buildUaGroupedCompareRowsDataset({
        source: args.source,
        rows: [...groupedCurrentRows, ...groupedPreviousRows],
        groupLabelDomain,
        requestedMetricFamilies: args.requestedMetricFamilies,
      }),
    );
  }

  return datasets;
}
